import axios from 'axios'

import { http } from '../lib/http/client'

interface ApiResponse<T> {
  success: boolean
  message: string
  data: T
}

export interface StoreOrderPagination {
  current_page: number
  last_page: number
  per_page: number
  total: number
  from: number | null
  to: number | null
}

export interface StoreOrderSucursal {
  id: number
  code: string
  name: string
}

export interface StoreOrderAmounts {
  subtotal_amount: string
  discount_total_amount: string
  taxable_subtotal_amount: string
  tax_total_amount: string
  delivery_fee_amount: string
  regular_total_amount: string
  savings_total_amount: string
  total_amount: string
  loyalty_credit_applied_amount: string
  external_due_amount: string
}

export interface StoreOrderCancellation {
  id: number
  reason: string
  notes: string | null
  status: string
  resolution_reason: string | null
  requested_at: string | null
  resolved_at: string | null
  cancelled_at: string | null
  requester_type?: string
  requester_name?: string | null
  requested_by_user_id?: number | null
  resolved_by_user_id?: number | null
  resolver_name?: string | null
  approved_at?: string | null
}

export interface StoreOrderFulfillment {
  type: 'DELIVERY' | 'PICKUP'
  status: string
  sucursal: StoreOrderSucursal | null
  coverage: { id: number; name: string } | null
  delivery_fee_amount: string
  estimated_delivery_min_minutes: number | null
  estimated_delivery_max_minutes: number | null
  assigned_user_name: string | null
  assigned_to_user_id?: number | null
  ready_at: string | null
  assigned_at: string | null
  started_at: string | null
  delivered_at: string | null
  cancelled_at: string | null
  delivery_address?: {
    street: string
    exterior_number: string
    interior_number: string | null
    neighborhood: string
    postal_code: string
    city: string
    state: string
    country: string
    references: string | null
    contact_phone: string
  } | null
}

export interface StaffStoreOrderSummary {
  order_number: string
  status: string
  payment_status: string
  fulfillment_status: string
  fulfillment_type: 'DELIVERY' | 'PICKUP'
  currency_code: string
  customer: { name: string; phone: string; email: string }
  sucursal: StoreOrderSucursal | null
  amounts: StoreOrderAmounts
  loyalty: StoreOrderLoyalty | null
  placed_at: string | null
  created_at: string | null
  cancelled_at: string | null
  completed_at: string | null
  sale_generated_at: string | null
  is_simulated: boolean
  pickup_recipient_name: string | null
  payments?: StoreOrderPayment[]
  sale?: StoreOrderSale | null
  fulfillment?: StoreOrderFulfillment | null
  cancellations?: StoreOrderCancellation[]
  available_fulfillment_transitions: string[]
  can_cancel: boolean
}

export interface StoreOrderLoyalty {
  program_enabled?: boolean
  points_balance?: number | null
  lifetime_points?: number | null
  current_tier?: {
    code: string
    name: string
    medal: string
  } | null
  reward?: {
    id: number
    points_required: number
    credit_amount: string
  } | null
  credit_generated?: string
  credit_applied?: string
  points_to_earn?: number
}

export interface StoreOrderReservation {
  id: number
  status: string
  quantity: number
  sucursal_id: number
  source_type: string
  product_batch_id: number | null
  batch_number_snapshot: string | null
  expiration_date_snapshot: string | null
  consumed_at: string | null
  released_at: string | null
  cancelled_at: string | null
}

export interface StoreOrderItem {
  product_id: number
  product: {
    name: string
    sku: string | null
    barcode: string | null
    image_url: string | null
    unit: { name: string; abbreviation: string } | null
  }
  quantity: number
  promotion_id: number | null
  pricing: unknown
  tax: unknown
  amounts: {
    regular_unit_subtotal_amount: string
    regular_unit_total_amount: string
    applied_unit_subtotal_amount: string
    applied_unit_tax_amount: string
    applied_unit_total_amount: string
    subtotal_amount: string
    discount_amount: string
    taxable_subtotal_amount: string
    tax_amount: string
    total_amount: string
    regular_total_amount: string
    savings_amount: string
  }
  reservations?: StoreOrderReservation[]
}

export interface StoreOrderPayment {
  type: string
  method: { code: string | null; name: string | null }
  amount: string
  status: string
  cash_tendered_amount: string | null
  expected_change_amount: string | null
  paid_at: string | null
  cancelled_at: string | null
  metadata: {
    transaction_id?: string | number | null
    [key: string]: unknown
  } | null
}

export interface StoreOrderPaymentCheckout {
  provider: 'mercadopago' | string
  preference_id: string | null
  checkout_url: string | null
  expires_at: string | null
  status: string
}

export interface StoreOrderEvent {
  id: number
  event_type: string
  actor_type: string
  actor_name: string | null
  from_order_status: string | null
  to_order_status: string | null
  from_fulfillment_status: string | null
  to_fulfillment_status: string | null
  from_payment_status?: string | null
  to_payment_status?: string | null
  metadata: {
    previous_payment_status?: string | null
    new_payment_status?: string | null
    [key: string]: unknown
  } | null
  occurred_at: string | null
}

export interface StaffStoreOrderDetail extends StaffStoreOrderSummary {
  items: StoreOrderItem[]
  payments: StoreOrderPayment[]
  events: StoreOrderEvent[]
  payment_checkout?: StoreOrderPaymentCheckout | null
  loyalty_hold_block_reason: string | null
  loyalty_hold?: {
    status: string
    points_reserved: number
    credit_reserved_amount: string
  } | null
  cash_collection?: StoreOrderCashCollection | null
}

export interface StoreOrderSale {
  id?: number
  ticket_number: string
  status: string
  is_simulated: boolean
  completed_at: string | null
  receivable_adjustments?: Array<{
    id: number
    sale_return_id: number
    amount: string
    balance_before: string
    balance_after: string
    occurred_at: string | null
  }>
  payments?: Array<{
    id: number
    method: string | null
    amount: string
    original_expected_amount: string | null
    status: string
    cash_received_amount: string | null
    change_amount: string | null
  }>
  cash_movements?: Array<{
    id: number
    type: string
    direction: string
    amount: string
    cash_register_session_id: number
    occurred_at: string | null
  }>
  returns?: Array<{
    id: number
    return_number: string
    status: string
    total_refund_amount: string
    external_refund_amount: string
  }>
}

export interface StoreOrderCashRemittance {
  id: number
  amount: string
  cash_register_session_id: number
  cash_register_movement_id: number
  received_by_user_id: number
  received_at: string | null
}

export interface StoreOrderCashDiscrepancy {
  id: number
  amount: string
  reason: string
  resolved_by_user_id: number
  resolved_at: string | null
}

export interface StoreOrderCashCollection {
  id: number
  collector_name: string
  recorded_by_user_id: number | null
  recorded_by_name: string | null
  recording_method: 'COURIER' | 'ADMIN'
  administrative_reason: string | null
  recipient_name: string | null
  amount: string
  cash_received_amount: string
  change_amount: string
  status: string
  remitted_amount: string
  shortfall_amount: string
  collected_at: string | null
  reconciled_at: string | null
  remittances: StoreOrderCashRemittance[]
  discrepancies: StoreOrderCashDiscrepancy[]
}

export interface StoreOrderCashCollectionSummary {
  id: number
  order_number: string | null
  sucursal_id: number | null
  status: string
  amount: string
  remitted_amount: string
  shortfall_amount: string
  collected_at: string | null
}

export interface StoreOrderListFilters {
  page?: number
  per_page?: number
  sucursal_id?: number
  placed_from?: string
  placed_to?: string
  status?: string
  payment_status?: string
  fulfillment_status?: string
  fulfillment_type?: string
  cancellation_status?: string
  cash_pending?: boolean
}

export interface StaffCancellationQueueItem {
  id: number
  order_number: string
  sucursal: StoreOrderSucursal | null
  requester_type: string
  requester_name: string | null
  reason: string
  notes: string | null
  status: string
  resolution_reason: string | null
  requested_at: string | null
  resolved_at: string | null
}

export interface DeliveryUser {
  id: number
  name: string
  last_name: string
  phone_number: string | null
}

export async function listStaffStoreOrders(filters: StoreOrderListFilters) {
  const params = {
    ...filters,
    ...(filters.cash_pending !== undefined
      ? { cash_pending: filters.cash_pending ? 1 : 0 }
      : {}),
  }
  const response = await http.get<
    ApiResponse<{
      orders: StaffStoreOrderSummary[]
      pagination: StoreOrderPagination
    }>
  >('/ecommerce/orders', { params })
  return response.data.data
}

export async function getStaffStoreOrder(orderNumber: string) {
  const response = await http.get<
    ApiResponse<{ order: StaffStoreOrderDetail }>
  >(`/ecommerce/orders/${encodeURIComponent(orderNumber)}`)
  return response.data.data.order
}

export async function listDeliveryUsers(sucursalId: number) {
  const response = await http.get<
    ApiResponse<{ delivery_users: DeliveryUser[] }>
  >('/ecommerce/orders/delivery-users', { params: { sucursal_id: sucursalId } })
  return response.data.data.delivery_users
}

export async function changeStoreOrderFulfillment(
  orderNumber: string,
  input: { status: string; assigned_to_user_id?: number },
) {
  return mutateOrder(
    'patch',
    `/ecommerce/orders/${encodeURIComponent(orderNumber)}/fulfillment`,
    input,
  )
}

export async function cancelStaffStoreOrder(
  orderNumber: string,
  input: { reason: string; notes?: string },
) {
  return mutateOrder(
    'post',
    `/ecommerce/orders/${encodeURIComponent(orderNumber)}/cancellations`,
    input,
  )
}

export async function listStaffCancellationRequests(filters: {
  page?: number
  per_page?: number
  sucursal_id?: number
  status?: string
}) {
  const response = await http.get<
    ApiResponse<{
      cancellations: StaffCancellationQueueItem[]
      pagination: StoreOrderPagination
    }>
  >('/ecommerce/orders/cancellations', { params: filters })
  return response.data.data
}

export async function decideStaffCancellation(
  orderNumber: string,
  cancellationId: number,
  decision: 'approve' | 'reject',
  reason?: string,
) {
  return mutateOrder(
    'post',
    `/ecommerce/orders/${encodeURIComponent(orderNumber)}/cancellations/${cancellationId}/${decision}`,
    reason ? { reason } : {},
  )
}

export async function collectStoreOrderCash(
  orderNumber: string,
  input: { cash_received_amount: string },
) {
  const response = await http.post<
    ApiResponse<{
      collection: Pick<
        StoreOrderCashCollection,
        | 'id'
        | 'amount'
        | 'cash_received_amount'
        | 'change_amount'
        | 'status'
        | 'collected_at'
      >
      replayed: boolean
    }>
  >(
    `/ecommerce/orders/${encodeURIComponent(orderNumber)}/cash-collection`,
    input,
    {
      headers: { 'Idempotency-Key': createIdempotencyKey() },
    },
  )
  return response.data.data
}

export async function collectStoreOrderCashAdministratively(
  orderNumber: string,
  input: { cash_received_amount: string; reason: string },
) {
  const response = await http.post<
    ApiResponse<{ collection: StoreOrderCashCollection; replayed: boolean }>
  >(
    `/ecommerce/orders/${encodeURIComponent(orderNumber)}/cash-collection/admin`,
    input,
    { headers: { 'Idempotency-Key': createIdempotencyKey() } },
  )
  return response.data.data
}

export async function completeStaffStoreOrder(orderNumber: string) {
  const response = await http.post<
    ApiResponse<{
      order: StaffStoreOrderDetail
      sale: StoreOrderSale
      replayed: boolean
    }>
  >(
    `/ecommerce/orders/${encodeURIComponent(orderNumber)}/complete`,
    {},
    {
      headers: { 'Idempotency-Key': createIdempotencyKey() },
    },
  )
  return response.data.data.order
}

export async function finishStaffStoreOrder(orderNumber: string) {
  const response = await http.post<
    ApiResponse<{ order: StaffStoreOrderDetail }>
  >(
    `/ecommerce/orders/${encodeURIComponent(orderNumber)}/complete-order`,
    {},
    { headers: { 'Idempotency-Key': createIdempotencyKey() } },
  )
  return response.data.data.order
}

export async function depositStoreOrderPickupCash(
  orderNumber: string,
  cashReceivedAmount: string,
) {
  const response = await http.post<
    ApiResponse<{ order: StaffStoreOrderDetail }>
  >(
    `/ecommerce/orders/${encodeURIComponent(orderNumber)}/pickup-cash-deposit`,
    { cash_received_amount: cashReceivedAmount },
    { headers: { 'Idempotency-Key': createIdempotencyKey() } },
  )
  return response.data.data.order
}

export async function listStoreOrderCashCollections(filters: {
  page?: number
  per_page?: number
  status?: string
  sucursal_id?: number
}) {
  const response = await http.get<
    ApiResponse<{
      collections: StoreOrderCashCollectionSummary[]
      pagination: Omit<StoreOrderPagination, 'from' | 'to'>
    }>
  >('/ecommerce/cash-collections', { params: filters })
  const { pagination } = response.data.data
  const from =
    pagination.total === 0
      ? null
      : (pagination.current_page - 1) * pagination.per_page + 1
  const to =
    pagination.total === 0
      ? null
      : Math.min(
        pagination.current_page * pagination.per_page,
        pagination.total,
      )
  return {
    collections: response.data.data.collections,
    pagination: { ...pagination, from, to } satisfies StoreOrderPagination,
  }
}

export async function remitStoreOrderCash(
  collectionId: number,
  amount: string,
) {
  const response = await http.post<
    ApiResponse<{ remittance: StoreOrderCashRemittance; replayed: boolean }>
  >(
    `/ecommerce/cash-collections/${collectionId}/remittances`,
    { amount },
    { headers: { 'Idempotency-Key': createIdempotencyKey() } },
  )
  return response.data.data
}

export async function resolveStoreOrderCashDiscrepancy(
  collectionId: number,
  input: { amount: string; reason: string },
) {
  const response = await http.post<
    ApiResponse<{ discrepancy: StoreOrderCashDiscrepancy; replayed: boolean }>
  >(`/ecommerce/cash-collections/${collectionId}/discrepancies`, input, {
    headers: { 'Idempotency-Key': createIdempotencyKey() },
  })
  return response.data.data
}

async function mutateOrder(
  method: 'post' | 'patch',
  url: string,
  data: unknown,
): Promise<StaffStoreOrderDetail> {
  const response = await http.request<
    ApiResponse<{ order: StaffStoreOrderDetail; replayed: boolean }>
  >({
    method,
    url,
    data,
    headers: { 'Idempotency-Key': createIdempotencyKey() },
  })
  return response.data.data.order
}

export function getStaffStoreOrdersProblem(error: unknown, fallback: string) {
  if (!axios.isAxiosError(error)) return { message: fallback }
  const payload = error.response?.data as
    | {
      message?: string
      errors?: Record<string, string | string[] | undefined>
    }
    | undefined
  const validationMessage = payload?.errors
    ? Object.values(payload.errors).flatMap((value) =>
      Array.isArray(value) ? value : value ? [value] : [],
    )[0]
    : undefined
  return {
    message:
      (typeof validationMessage === 'string' && validationMessage) ||
      payload?.message ||
      fallback,
    status: error.response?.status,
  }
}

export function formatStoreOrderMoney(value: string, currency = 'MXN') {
  const amount = Number.parseFloat(value)
  if (!Number.isFinite(amount)) return 'Por confirmar'
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: currency || 'MXN',
  }).format(amount)
}

export function getStaffOrderStatusLabel(status: string) {
  return (
    {
      CONFIRMED: 'Confirmado',
      PREPARING: 'En preparación',
      READY_FOR_PICKUP: 'Listo para recoger',
      OUT_FOR_DELIVERY: 'En ruta',
      COMPLETED: 'Venta generada',
      COMPLETED_ORDER: 'Pedido completado',
      CANCELLED: 'Cancelado',
      PENDING: 'Pendiente',
      PARTIALLY_PAID: 'Pagado parcialmente',
      PAID: 'Pagado',
      REJECTED: 'Rechazado',
      CREATING: 'Preparando pago',
      EXPIRED: 'Vencido',
      REFUND_PENDING: 'Reembolso pendiente',
      SIMULATED: 'Simulado',
      PARTIALLY_REFUNDED: 'Reembolsada parcialmente',
      REFUNDED: 'Reembolsada',
      AUTHORIZED: 'Autorizado',
      ASSIGNED: 'Repartidor asignado',
      DELIVERED: 'Entregado',
      REQUESTED: 'Pendiente',
      APPROVED: 'Aprobada',
      ACTIVE: 'Activa',
      CONSUMED: 'Consumida',
      RELEASED: 'Liberada',
      IN_TRANSIT: 'Efectivo en tránsito',
      PARTIALLY_REMITTED: 'Rendido parcialmente',
      RECONCILED: 'Conciliado',
      RECONCILED_WITH_SHORTFALL: 'Conciliado con diferencia',
    }[status] || 'Estado por confirmar'
  )
}

/**
 * Clases Tailwind para el badge de estado de PEDIDO (fulfillment/order).
 */
export function getOrderStatusBadgeClass(status: string): string {
  return (
    ({
      CONFIRMED: 'bg-blue-100 text-blue-800',
      PREPARING: 'bg-amber-100 text-amber-800',
      READY_FOR_PICKUP: 'bg-violet-100 text-violet-800',
      OUT_FOR_DELIVERY: 'bg-indigo-100 text-indigo-800',
      ASSIGNED: 'bg-indigo-100 text-indigo-800',
      DELIVERED: 'bg-teal-100 text-teal-800',
      COMPLETED: 'bg-green-100 text-green-800',
      COMPLETED_ORDER: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-red-100 text-red-800',
      SIMULATED: 'bg-gray-100 text-gray-600',
    } as Record<string, string>)[status] ?? 'bg-slate-100 text-slate-700'
  )
}

/**
 * Clases Tailwind para el badge de ESTADO DE PAGO.
 */
export function getPaymentStatusBadgeClass(status: string): string {
  return (
    ({
      PAID: 'bg-green-100 text-green-800',
      PARTIALLY_PAID: 'bg-amber-100 text-amber-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      CANCELLED: 'bg-red-100 text-red-800',
      REFUNDED: 'bg-purple-100 text-purple-800',
      PARTIALLY_REFUNDED: 'bg-purple-100 text-purple-700',
      SIMULATED: 'bg-gray-100 text-gray-600',
      REJECTED: 'bg-red-100 text-red-800',
    } as Record<string, string>)[status] ?? 'bg-slate-100 text-slate-700'
  )
}

/** Opciones para filtrar pedidos por su estado de pago real. */
export const PAYMENT_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pendiente' },
  { value: 'PAID', label: 'Pagado' },
  { value: 'PARTIALLY_PAID', label: 'Pagado parcialmente' },
  { value: 'CANCELLED', label: 'Cancelado' },
  { value: 'REJECTED', label: 'Rechazado' },
  { value: 'SIMULATED', label: 'Simulado' },
] as const

export function getStoreOrderEventLabel(eventType: string) {
  return (
    {
      ORDER_CONFIRMED: 'Pedido confirmado',
      ORDER_PREPARING: 'Preparación iniciada',
      ORDER_READY_FOR_PICKUP: 'Pedido listo para recoger',
      ORDER_ASSIGNED: 'Repartidor asignado',
      ORDER_REASSIGNED: 'Repartidor reasignado',
      ORDER_OUT_FOR_DELIVERY: 'Pedido en ruta',
      ORDER_DELIVERED: 'Pedido entregado',
      CANCELLATION_REQUESTED: 'Cancelación solicitada',
      CANCELLATION_APPROVED: 'Cancelación aprobada',
      CANCELLATION_REJECTED: 'Cancelación rechazada',
      CANCELLATION_COMPLETED: 'Cancelación completada',
      DEMO_PAYMENT_VOIDED: 'Pago anulado',
      CASH_COLLECTED: 'Efectivo cobrado',
      CASH_COLLECTION_ADMIN_RECORDED: 'Cobro informado registrado por administración',
      OPENPAY_PAYMENT_VERIFIED: 'Pago OpenPay verificado',
      SALE_COMPLETED: 'Ticket de venta generado',
      SALE_SIMULATED: 'Ticket simulado generado',
      SALE_VOIDED: 'Ticket anulado',
      SALE_REFUND_VERIFIED: 'Devolución verificada',
      ORDER_STATUS_RECLASSIFIED: 'Pedido histórico reclasificado',
      ORDER_COMPLETED: 'Pedido completado',
      ORDER_SIMULATED: 'Pedido de prueba completado',
      CASH_REMITTED: 'Efectivo rendido en caja',
      CASH_DISCREPANCY_RESOLVED: 'Diferencia de efectivo registrada',
      ORDER_PAYMENT_STATUS_UPDATED: 'Estado de pago actualizado',
      MP_PAYMENT_APPROVED: 'Pago en línea aprobado',
      MP_PAYMENT_REJECTED: 'Pago en línea rechazado',
      MP_PAYMENT_EXPIRED: 'Pago en línea vencido',
    }[eventType] || 'Evento registrado'
  )
}

export function getStoreOrderPaymentTypeLabel(type: string) {
  return (
    {
      CASH: 'Efectivo',
      PAYMENT_GATEWAY: 'Pago en línea',
      LOYALTY_POINTS: 'Puntos de lealtad',
    }[type] || 'Pago registrado'
  )
}

export function formatStoreOrderDate(value: string | null) {
  if (!value) return { date: 'Por confirmar', time: '' }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return { date: 'Por confirmar', time: '' }
  return {
    date: date.toLocaleDateString('es-MX'),
    time: date.toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  }
}

function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (value) => {
    const random = (Math.random() * 16) | 0
    const digit = value === 'x' ? random : (random & 0x3) | 0x8
    return digit.toString(16)
  })
}
export async function updateStoreOrderPaymentStatus(
  orderNumber: string,
  paymentStatus: string,
): Promise<StaffStoreOrderDetail> {
  const response = await http.patch<
    ApiResponse<{ order: StaffStoreOrderDetail }>
  >(`/ecommerce/orders/${encodeURIComponent(orderNumber)}/payment-status`, {
    payment_status: paymentStatus,
  })
  return response.data.data.order
}
